import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../hooks/useLanguage';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { InteractiveMap } from '../components/InteractiveMap';
import { clientLocations } from '../data/mapLocations';
import { PartnersGrid } from '../components/PartnersGrid';
// Import officeLocations if used in the component
import { officeLocations } from '../data/mapLocations';
export function PartnersPage() {
  const {
    t,
    setLocale,
    direction
  } = useLanguage();
  const location = useLocation();
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useScrollReveal();
  const {
    ref: descRef,
    isVisible: descVisible
  } = useScrollReveal();
  const {
    ref: mapRef,
    isVisible: mapVisible
  } = useScrollReveal();
  const [activeTab, setActiveTab] = useState<'clients' | 'offices'>('clients');
  useEffect(() => {
    if (location.pathname.startsWith('/ar')) {
      setLocale('ar');
    } else {
      setLocale('en');
    }
    window.scrollTo(0, 0);
  }, [location.pathname, setLocale]);
  // Change Header variant from "dark" to "light"
  return <div className="min-h-screen w-full bg-white text-slate-900" dir={direction} data-id="element-1015">
      {/* Changed Header variant from "dark" to "light" */}
      <Header variant="transparent-dark" data-id="element-1016" />

      <main data-id="element-1017">
        {/* Wrapper for hero SVG spanning hero + next sections */}
        <div className="relative" data-id="element-1018">
          {/* 1. Hero Banner */}
          <section className="relative min-h-screen overflow-hidden" ref={heroRef} aria-label="Partners Hero" data-id="element-1019">
            {/* Background Image */}
            <div className="absolute inset-0 bg-cover bg-center" role="presentation" aria-hidden="true" style={{
            backgroundImage: "url('https://cdn.magicpatterns.com/uploads/p5pVgXKQW9b6vL7vTUuVRQ/partners.jpg')"
          }} data-id="element-1020" />

            {/* Overlay for better text readability */}
            <div className="absolute inset-0 bg-black/20" data-id="element-1021" />

            <div className="relative z-10 w-full min-h-screen flex items-center" data-id="element-1022">
              <div className="container mx-auto px-6 md:px-12" data-id="element-1023">
                <div className="max-w-3xl" data-id="element-1024">
                  <h1 className={`text-[#061D76] font-semibold leading-[1.05] mb-10 uppercase text-[32px] md:text-[44px] lg:text-[55px] whitespace-pre-line reveal-hidden ${heroVisible ? 'animate-fade-up' : ''}`} data-id="element-1025">
                    {t('partnersPage.hero.title')}
                  </h1>
                  <p className={`text-base md:text-lg text-[#061D76] mb-10 max-w-xl leading-relaxed reveal-hidden ${heroVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-1026">
                    {t('partnersPage.hero.subtitle')}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 2. Description Section - Dark gradient with text only */}
          <section className="section-inner gradient-dark-horizontal text-white relative overflow-hidden z-[2]" ref={descRef} aria-label="Partners Overview" data-id="element-1027">
            <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-1028">
              <div className="max-w-5xl space-y-8" data-id="element-1029">
                <p className={`text-lg md:text-xl leading-relaxed reveal-hidden ${descVisible ? 'animate-fade-up' : ''}`} data-id="element-1030">
                  {t('partnersPage.description.paragraph1')}
                </p>
                <p className={`text-lg md:text-xl leading-relaxed reveal-hidden ${descVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-1031">
                  {t('partnersPage.description.paragraph2')}
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* 3. Partners Grid Section with Filters */}
        <PartnersGrid data-id="element-1032" />

        {/* 4. Map Section - Reusing InteractiveMap */}
        <section className="gradient-light relative overflow-hidden" ref={mapRef} aria-label="Global Presence Map" style={{
        paddingTop: '4rem',
        paddingBottom: '4rem',
        minHeight: '70vh',
        overflow: 'visible'
      }} data-id="element-1033">
          {/* Top-left decorative SVG */}
          <img src="https://cdn.magicpatterns.com/uploads/sjKgqbQqigcz9DMA9Uxgwc/top-left-csc-offers.svg" alt="" role="presentation" aria-hidden="true" className="absolute top-0 left-0 w-[300px] md:w-[500px] h-auto pointer-events-none opacity-10" data-id="element-1034" />

          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-1035">
            {/* Tabs */}
            <div className={`flex gap-6 mb-8 reveal-hidden ${mapVisible ? 'animate-fade-up' : ''}`} data-id="element-1036">
              <button onClick={() => setActiveTab('clients')} className={`text-base md:text-lg font-semibold transition-colors ${activeTab === 'clients' ? 'text-[#061D76] underline underline-offset-4' : 'text-[#25B8D6] hover:text-[#061D76]'}`} data-id="element-1037">
                {t('contactPage.map.ourClients')}
              </button>
              <button onClick={() => setActiveTab('offices')} className={`text-base md:text-lg font-semibold transition-colors ${activeTab === 'offices' ? 'text-[#061D76] underline underline-offset-4' : 'text-[#25B8D6] hover:text-[#061D76]'}`} data-id="element-1038">
                {t('contactPage.map.ourOffices')}
              </button>
            </div>

            {/* Map Container */}
            <div className={`relative w-full reveal-hidden ${mapVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-1039">
              <InteractiveMap locations={activeTab === 'clients' ? clientLocations : officeLocations} mapSrc="https://cdn.magicpatterns.com/uploads/24yKzhQTsKSKj2Z6kqe6yX/map.svg" data-id="element-1040" />
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1041" />
    </div>;
}